import { useEffect, useRef, useCallback } from 'react';
import Tribute from 'tributejs';
import { searchUsers, type UserSearchResult } from '../api/userSearch';

export interface MentionData {
  user_id: string;
  name: string;
}

interface UseTributeOptions {
  /** Called whenever the list of tracked mentions changes */
  onMentionsChange: (mentions: MentionData[]) => void;
  /** Called when tribute.js modifies the textarea value (e.g. after selecting a mention).
   *  Use this to sync React controlled state with the DOM value tribute inserted. */
  onTextChange?: (value: string) => void;
  /** Pass a value that changes when the textarea mounts/unmounts (e.g. isExpanded).
   *  The effect will re-run when this changes so tribute can attach to the new DOM node. */
  attachDep?: unknown;
}

/** Escape HTML special characters to prevent XSS in tribute.js menu items */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * React hook that attaches tribute.js @mention autocomplete to a textarea.
 *
 * Usage:
 *   const { textareaRef, clearMentions, syncMentionsWithText } = useTribute({
 *     onMentionsChange: (mentions) => setMentions(mentions),
 *   });
 *
 *   <textarea ref={textareaRef} onChange={(e) => { setValue(e.target.value); syncMentionsWithText(e.target.value); }} />
 */
export function useTribute(options: UseTributeOptions) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tributeRef = useRef<Tribute<UserSearchResult> | null>(null);
  const mentionsRef = useRef<Map<string, MentionData>>(new Map());

  const { onMentionsChange, onTextChange, attachDep } = options;

  // Stable callback to notify parent of mention changes
  const notifyMentions = useCallback(() => {
    onMentionsChange(Array.from(mentionsRef.current.values()));
  }, [onMentionsChange]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Cache search results per query to avoid repeated network calls
    const searchCache = new Map<string, UserSearchResult[]>();
    // Track in-flight requests so we can abort superseded ones
    let lastRequestId = 0;
    let lastAbortController: AbortController | null = null;

    const tribute = new Tribute<UserSearchResult>({
      trigger: '@',
      // Do NOT use allowSpaces — Tribute has a /g regex bug in getTriggerInfo
      // where the second regex.test() misses the \u00A0 terminator that the
      // first test() already consumed (lastIndex advances past it).
      // Without allowSpaces, a regular space terminates the mention scan,
      // so typing after "@Name " won't re-trigger the autocomplete.
      allowSpaces: false,
      menuShowMinLength: 1,
      values: (text: string, cb: (results: UserSearchResult[]) => void) => {
        if (text.length < 1) {
          cb([]);
          return;
        }

        // Return cached results immediately if available
        const cached = searchCache.get(text);
        if (cached) {
          cb(cached);
          return;
        }

        // No debounce — fire the API call immediately.
        // Debouncing caused a timing gap where Tribute would show "No users found"
        // because Tribute's processValues can hide/deactivate the menu on empty
        // results, so delivering stale empty results before real ones is unreliable.
        //
        // To reduce network chatter, we abort the previous in-flight request
        // before starting a new one. Only the latest invocation's cb() is called;
        // superseded invocations are intentionally left unfulfilled — Tribute
        // discards them once a newer values() call is made.
        if (lastAbortController) {
          lastAbortController.abort();
        }
        const abortController = new AbortController();
        lastAbortController = abortController;
        const requestId = ++lastRequestId;
        searchUsers(text, 10, abortController.signal)
          .then((results) => {
            if (requestId !== lastRequestId) return;
            searchCache.set(text, results);
            cb(results);
          })
          .catch(() => {
            if (requestId !== lastRequestId) return;
            // Don't surface abort errors — only call cb on genuine failures
            if (abortController.signal.aborted) return;
            cb([]);
          });
      },
      lookup: (item: UserSearchResult) => `${item.name} ${item.email}`,
      fillAttr: 'name',
      selectTemplate: (item) => {
        if (!item) return '';
        return `@${item.original.name}`;
      },
      menuItemTemplate: (item) => {
        if (!item) return '';
        const safeName = escapeHtml(item.original.name);
        const safeEmail = escapeHtml(item.original.email);
        return `<span style="font-weight:500">${safeName}</span> <span style="color:#9ca3af;font-size:0.75rem">${safeEmail}</span>`;
      },
      noMatchTemplate: () =>
        '<li style="padding:8px 12px;color:#9ca3af;font-size:0.875rem">No users found</li>',
    });

    tribute.attach(textarea);
    tributeRef.current = tribute;

    // Listen for tribute-replaced event to track mentions
    const handleReplaced = (e: Event) => {
      const customEvent = e as CustomEvent;
      const detail = customEvent.detail?.item?.original as UserSearchResult | undefined;
      if (detail) {
        mentionsRef.current.set(detail.id, {
          user_id: detail.id,
          name: detail.name,
        });
        notifyMentions();
      }

      // Tribute.js directly mutates the textarea DOM value after selecting a mention.
      // React's controlled component (value={state}) will overwrite this on the next
      // render unless we sync the DOM value back into React state.
      if (onTextChange && textarea) {
        onTextChange(textarea.value);
      }
    };

    // Prevent Tribute from intercepting Shift+Enter — the user expects it to
    // insert a newline, but Tribute treats ALL Enter presses as "select item"
    // when the menu is active (it doesn't check modifier keys).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && e.shiftKey && tribute.isActive) {
        // Hide the menu temporarily so Tribute's own keydown handler sees
        // isActive=false and won't preventDefault/select the highlighted item.
        // The menu will reappear on the next keystroke if still relevant.
        // hideMenu exists at runtime but isn't in Tribute's type definitions.
        // Feature-detect to guard against future API changes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyTribute = tribute as any;
        if (typeof anyTribute.hideMenu === 'function') {
          anyTribute.hideMenu();
        }
      }
    };

    textarea.addEventListener('tribute-replaced', handleReplaced);
    textarea.addEventListener('keydown', handleKeyDown, true); // capture phase — runs before Tribute's handler

    return () => {
      textarea.removeEventListener('tribute-replaced', handleReplaced);
      textarea.removeEventListener('keydown', handleKeyDown, true);
      tribute.detach(textarea);
    };
    // Re-run when attachDep changes (e.g. when textarea mounts after expand)
  }, [notifyMentions, onTextChange, attachDep]);

  /** Call after submit to clear tracked mentions */
  const clearMentions = useCallback(() => {
    mentionsRef.current.clear();
    notifyMentions();
  }, [notifyMentions]);

  /** Call on text change to prune mentions whose @Name was removed */
  const syncMentionsWithText = useCallback(
    (text: string) => {
      let changed = false;
      for (const [userId, mention] of mentionsRef.current) {
        if (!text.includes(`@${mention.name}`)) {
          mentionsRef.current.delete(userId);
          changed = true;
        }
      }
      if (changed) {
        notifyMentions();
      }
    },
    [notifyMentions],
  );

  /** Check whether the tribute autocomplete menu is currently open */
  const isMenuOpen = useCallback(() => {
    return tributeRef.current?.isActive ?? false;
  }, []);

  return { textareaRef, clearMentions, syncMentionsWithText, isMenuOpen };
}
